import React, { useState, useEffect } from 'react';
import { invoicesAPI, clientsAPI, paymentsAPI, timeEntriesAPI, casesAPI } from '../lib/api';
import { formatCurrency, formatDate, getStatusColor, downloadFile, openFileInNewTab } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  Receipt, 
  Plus, 
  Send,
  CreditCard,
  Eye,
  Clock,
  Check,
  X,
  Trash2,
  FileText,
  Download,
  DollarSign,
  AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { PageLoader } from '../components/common/LoadingSpinner';
import { FeatureGate } from '../components/common/FeatureGate';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { cn } from '../lib/utils';

export const Invoices = () => {
  const { hasFeature } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [cases, setCases] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  
  // Send Invoice Preview Dialog
  const [sendPreviewOpen, setSendPreviewOpen] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(null);
  const [sendEmailTo, setSendEmailTo] = useState('');
  const [sendSubject, setSendSubject] = useState('');
  const [sendMessage, setSendMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  
  // Form state
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedCase, setSelectedCase] = useState('');
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [manualLineItems, setManualLineItems] = useState([]);
  const [invoiceNotes, setInvoiceNotes] = useState('');
  const [dueDate, setDueDate] = useState('');

  useEffect(() => {
    if (hasFeature('time_billing')) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [hasFeature]);

  const fetchData = async () => {
    try {
      const [invoicesRes, clientsRes, casesRes, entriesRes] = await Promise.all([
        invoicesAPI.getAll(),
        clientsAPI.getAll(),
        casesAPI.getAll(),
        timeEntriesAPI.getAll().catch(() => ({ data: [] }))
      ]);
      setInvoices(invoicesRes.data);
      setClients(clientsRes.data);
      setCases(casesRes.data);
      // Filter unbilled time entries
      setTimeEntries((entriesRes.data || []).filter(e => !e.invoiced && e.billable));
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter time entries by selected client/case
  const filteredTimeEntries = timeEntries.filter(entry => {
    if (selectedClient && entry.client_id !== selectedClient) return false;
    if (selectedCase && entry.case_id !== selectedCase) return false;
    return true;
  });

  // Calculate totals
  const selectedEntriesTotal = selectedEntries.reduce((sum, entryId) => {
    const entry = timeEntries.find(e => e.entry_id === entryId);
    return sum + (entry ? entry.hours * (entry.rate || 0) : 0);
  }, 0);

  const manualItemsTotal = manualLineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  const invoiceTotal = selectedEntriesTotal + manualItemsTotal;

  const handleToggleEntry = (entryId) => {
    setSelectedEntries(prev => 
      prev.includes(entryId) 
        ? prev.filter(id => id !== entryId)
        : [...prev, entryId]
    );
  };

  const handleSelectAllEntries = () => {
    if (selectedEntries.length === filteredTimeEntries.length) {
      setSelectedEntries([]);
    } else {
      setSelectedEntries(filteredTimeEntries.map(e => e.entry_id));
    }
  };

  const handleAddManualItem = () => {
    setManualLineItems([...manualLineItems, { description: '', amount: '', quantity: 1 }]);
  };

  const handleRemoveManualItem = (index) => {
    setManualLineItems(manualLineItems.filter((_, i) => i !== index));
  };

  const handleManualItemChange = (index, field, value) => {
    const updated = [...manualLineItems];
    updated[index][field] = value;
    setManualLineItems(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedClient) {
      toast.error('Please select a client');
      return;
    }
    
    if (selectedEntries.length === 0 && manualLineItems.length === 0) {
      toast.error('Please add at least one line item');
      return;
    }

    try {
      // Build line items from selected time entries
      const timeLineItems = selectedEntries.map(entryId => {
        const entry = timeEntries.find(e => e.entry_id === entryId);
        return {
          type: 'time',
          entry_id: entryId,
          description: entry?.description || 'Legal services',
          hours: entry?.hours || 0,
          rate: entry?.rate || 0,
          amount: (entry?.hours || 0) * (entry?.rate || 0)
        };
      });

      // Add manual line items
      const otherLineItems = manualLineItems
        .filter(item => item.description && item.amount)
        .map(item => ({
          type: 'manual',
          description: item.description,
          quantity: parseFloat(item.quantity) || 1,
          amount: parseFloat(item.amount) || 0
        }));

      const invoiceData = {
        client_id: selectedClient,
        case_id: selectedCase && selectedCase !== 'none' ? selectedCase : null,
        line_items: [...timeLineItems, ...otherLineItems],
        amount: invoiceTotal,
        notes: invoiceNotes,
        due_date: dueDate ? new Date(dueDate).toISOString() : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
      };

      await invoicesAPI.create(invoiceData);
      toast.success('Invoice created successfully');
      fetchData();
      resetForm();
    } catch (error) {
      console.error('Failed to create invoice:', error);
      toast.error('Failed to create invoice');
    }
  };

  const handleSend = async (invoiceId) => {
    // Open the send preview dialog instead of sending directly
    const invoice = invoices.find(i => i.invoice_id === invoiceId);
    if (!invoice) return;
    
    const client = clients.find(c => c.client_id === invoice.client_id);
    setSendingInvoice(invoice);
    setSendEmailTo(client?.email || '');
    setSendSubject(`Invoice #${invoice.invoice_number || invoiceId.slice(-8).toUpperCase()} from LEAP Pro`);
    setSendMessage(`Dear ${client?.name || 'Client'},\n\nPlease find attached your invoice for our services.\n\nIf you have any questions, please don't hesitate to contact us.\n\nThank you for your business.`);
    setSendPreviewOpen(true);
  };

  const handleConfirmSend = async () => {
    if (!sendingInvoice) return;
    setIsSending(true);
    try {
      await invoicesAPI.send(sendingInvoice.invoice_id, {
        email: sendEmailTo,
        subject: sendSubject,
        message: sendMessage
      });
      toast.success('Invoice sent successfully');
      fetchData();
      setSendPreviewOpen(false);
      setSendingInvoice(null);
    } catch (error) {
      console.error('Failed to send invoice:', error);
      toast.error('Failed to send invoice');
    } finally {
      setIsSending(false);
    }
  };

  const handleDownloadPdf = async (invoice, openInTab = false) => {
    try {
      const response = await invoicesAPI.downloadPdf(invoice.invoice_id);
      const filename = `Invoice_${invoice.invoice_number || invoice.invoice_id.slice(-8).toUpperCase()}.pdf`;
      
      if (openInTab) {
        const success = openFileInNewTab(response.data, 'application/pdf');
        if (success) {
          toast.success('Invoice opened in new tab');
        } else {
          toast.error('Failed to open invoice');
        }
      } else {
        const success = downloadFile(response.data, filename, 'application/pdf');
        if (success) {
          toast.success('Invoice downloaded');
        } else {
          // Fallback to opening in new tab
          toast.info('Opening in new tab instead...');
          openFileInNewTab(response.data, 'application/pdf');
        }
      }
    } catch (error) {
      console.error('Failed to download invoice:', error);
      toast.error('Failed to download invoice PDF');
    }
  };

  const handlePayment = async (invoiceId) => {
    try {
      const originUrl = window.location.origin;
      const response = await paymentsAPI.createCheckout(invoiceId, originUrl);
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Failed to create checkout:', error);
      toast.error('Failed to create payment link');
    }
  };

  const resetForm = () => {
    setSelectedClient('');
    setSelectedCase('');
    setSelectedEntries([]);
    setManualLineItems([]);
    setInvoiceNotes('');
    setDueDate('');
    setIsDialogOpen(false);
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.client_id === clientId);
    return client?.name || 'Unknown';
  };

  const getCaseName = (caseId) => {
    const caseItem = cases.find(c => c.case_id === caseId);
    return caseItem?.title || '';
  };

  // Filter invoices by tab
  const filteredInvoices = activeTab === 'all' 
    ? invoices 
    : invoices.filter(inv => inv.status === activeTab);

  const totalBilled = invoices.reduce((sum, inv) => sum + (inv.amount || 0), 0);
  const totalPaid = invoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + (inv.amount || 0), 0);
  const totalOutstanding = invoices.filter(inv => inv.status === 'sent' || inv.status === 'overdue').reduce((sum, inv) => sum + (inv.amount || 0), 0);

  if (!hasFeature('time_billing')) {
    return (
      <FeatureGate feature="time_billing">
        <div className="h-[600px]" />
      </FeatureGate>
    );
  }

  if (loading) {
    return <PageLoader text="Loading invoices..." />;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="invoices-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Invoices</h1>
          <p className="text-slate-500 mt-1">Manage client invoices and payments</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setIsDialogOpen(true); }}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="create-invoice-btn">
              <Plus className="h-4 w-4" />
              Create Invoice
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Invoice</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              {/* Client & Case Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Client *</Label>
                  <Select value={selectedClient} onValueChange={setSelectedClient}>
                    <SelectTrigger data-testid="invoice-client-select">
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.client_id} value={client.client_id}>
                          {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Related Case (Optional)</Label>
                  <Select value={selectedCase} onValueChange={setSelectedCase}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select case" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {cases.map((c) => (
                        <SelectItem key={c.case_id} value={c.case_id}>
                          {c.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Time Entries Selection */}
              {filteredTimeEntries.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Unbilled Time Entries
                    </Label>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={handleSelectAllEntries}
                    >
                      {selectedEntries.length === filteredTimeEntries.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>
                  <div className="border rounded-lg max-h-[200px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10"></TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead className="text-right">Hours</TableHead>
                          <TableHead className="text-right">Rate</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredTimeEntries.map((entry) => (
                          <TableRow 
                            key={entry.entry_id}
                            className={cn(
                              "cursor-pointer",
                              selectedEntries.includes(entry.entry_id) && "bg-blue-50"
                            )}
                            onClick={() => handleToggleEntry(entry.entry_id)}
                          >
                            <TableCell>
                              <Checkbox 
                                checked={selectedEntries.includes(entry.entry_id)}
                                onCheckedChange={() => handleToggleEntry(entry.entry_id)}
                              />
                            </TableCell>
                            <TableCell className="text-sm">{formatDate(entry.date)}</TableCell>
                            <TableCell className="text-sm max-w-[200px] truncate">{entry.description}</TableCell>
                            <TableCell className="text-right text-sm">{entry.hours}h</TableCell>
                            <TableCell className="text-right text-sm">{formatCurrency(entry.rate || 0)}/hr</TableCell>
                            <TableCell className="text-right text-sm font-medium">
                              {formatCurrency(entry.hours * (entry.rate || 0))}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {selectedEntries.length > 0 && (
                    <div className="text-sm text-slate-600 flex justify-between px-2">
                      <span>{selectedEntries.length} entries selected</span>
                      <span className="font-medium">Subtotal: {formatCurrency(selectedEntriesTotal)}</span>
                    </div>
                  )}
                </div>
              )}

              {filteredTimeEntries.length === 0 && selectedClient && (
                <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">No unbilled time entries</p>
                    <p className="text-sm text-amber-700">There are no unbilled time entries for this client. You can add manual line items below.</p>
                  </div>
                </div>
              )}

              {/* Manual Line Items */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Additional Line Items
                  </Label>
                  <Button type="button" variant="outline" size="sm" onClick={handleAddManualItem}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add Item
                  </Button>
                </div>
                {manualLineItems.length > 0 && (
                  <div className="space-y-2">
                    {manualLineItems.map((item, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Input
                          placeholder="Description"
                          value={item.description}
                          onChange={(e) => handleManualItemChange(index, 'description', e.target.value)}
                          className="flex-1"
                        />
                        <Input
                          type="number"
                          placeholder="Amount"
                          value={item.amount}
                          onChange={(e) => handleManualItemChange(index, 'amount', e.target.value)}
                          className="w-32"
                          step="0.01"
                        />
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleRemoveManualItem(index)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Due Date & Notes */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Notes (Optional)</Label>
                  <Input
                    placeholder="Payment terms, thank you note, etc."
                    value={invoiceNotes}
                    onChange={(e) => setInvoiceNotes(e.target.value)}
                  />
                </div>
              </div>

              {/* Total */}
              <div className="p-4 rounded-lg bg-slate-50 border">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-700">Invoice Total</span>
                  <span className="text-2xl font-bold text-slate-900">{formatCurrency(invoiceTotal)}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                <Button type="submit" disabled={invoiceTotal <= 0} data-testid="save-invoice-btn">
                  Create Invoice
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Billed</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{formatCurrency(totalBilled)}</p>
              </div>
              <div className="p-2 rounded-lg bg-blue-100">
                <Receipt className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Paid</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{formatCurrency(totalPaid)}</p>
              </div>
              <div className="p-2 rounded-lg bg-green-100">
                <Check className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Outstanding</p>
                <p className="text-2xl font-bold text-amber-600 mt-1">{formatCurrency(totalOutstanding)}</p>
              </div>
              <div className="p-2 rounded-lg bg-amber-100">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Unbilled Time</p>
                <p className="text-2xl font-bold text-purple-600 mt-1">
                  {formatCurrency(timeEntries.reduce((sum, e) => sum + (e.hours * (e.rate || 0)), 0))}
                </p>
              </div>
              <div className="p-2 rounded-lg bg-purple-100">
                <DollarSign className="h-5 w-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Invoices List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Invoices</CardTitle>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="draft">Draft</TabsTrigger>
                <TabsTrigger value="sent">Sent</TabsTrigger>
                <TabsTrigger value="paid">Paid</TabsTrigger>
                <TabsTrigger value="overdue">Overdue</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInvoices.length > 0 ? (
                  filteredInvoices.map((invoice) => (
                    <TableRow key={invoice.invoice_id}>
                      <TableCell className="font-medium">{invoice.invoice_number || invoice.invoice_id?.slice(-8)}</TableCell>
                      <TableCell>{getClientName(invoice.client_id)}</TableCell>
                      <TableCell>{formatDate(invoice.created_at)}</TableCell>
                      <TableCell>{formatDate(invoice.due_date)}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(invoice.amount)}</TableCell>
                      <TableCell>
                        <Badge className={cn(
                          invoice.status === 'paid' && "bg-green-100 text-green-700",
                          invoice.status === 'sent' && "bg-blue-100 text-blue-700",
                          invoice.status === 'draft' && "bg-slate-100 text-slate-700",
                          invoice.status === 'overdue' && "bg-red-100 text-red-700"
                        )}>
                          {invoice.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setViewingInvoice(invoice)}
                            data-testid={`view-invoice-${invoice.invoice_id}`}
                            title="View"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadPdf(invoice)}
                            data-testid={`download-invoice-${invoice.invoice_id}`}
                            title="Download PDF"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          {invoice.status === 'draft' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSend(invoice.invoice_id)}
                              data-testid={`send-invoice-${invoice.invoice_id}`}
                            >
                              <Send className="h-4 w-4 mr-1" />
                              Send
                            </Button>
                          )}
                          {(invoice.status === 'sent' || invoice.status === 'overdue') && (
                            <Button
                              size="sm"
                              onClick={() => handlePayment(invoice.invoice_id)}
                              data-testid={`pay-invoice-${invoice.invoice_id}`}
                            >
                              <CreditCard className="h-4 w-4 mr-1" />
                              Pay
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                      <Receipt className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                      <p>No invoices found</p>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Invoice View Dialog */}
      <Dialog open={!!viewingInvoice} onOpenChange={() => setViewingInvoice(null)}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Invoice Details</DialogTitle>
          </DialogHeader>
          {viewingInvoice && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Invoice Number</p>
                  <p className="font-medium">{viewingInvoice.invoice_number || viewingInvoice.invoice_id?.slice(-8)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Status</p>
                  <Badge className={cn(
                    viewingInvoice.status === 'paid' && "bg-green-100 text-green-700",
                    viewingInvoice.status === 'sent' && "bg-blue-100 text-blue-700",
                    viewingInvoice.status === 'draft' && "bg-slate-100 text-slate-700",
                    viewingInvoice.status === 'overdue' && "bg-red-100 text-red-700"
                  )}>
                    {viewingInvoice.status}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Client</p>
                  <p className="font-medium">{getClientName(viewingInvoice.client_id)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Due Date</p>
                  <p className="font-medium">{formatDate(viewingInvoice.due_date)}</p>
                </div>
              </div>
              
              {viewingInvoice.line_items && viewingInvoice.line_items.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {viewingInvoice.line_items.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            {item.description}
                            {item.hours && <span className="text-slate-500 ml-1">({item.hours}h @ {formatCurrency(item.rate)}/hr)</span>}
                          </TableCell>
                          <TableCell className="text-right">{formatCurrency(item.amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              
              <div className="p-4 rounded-lg bg-slate-50 flex justify-between items-center">
                <span className="font-medium">Total</span>
                <span className="text-2xl font-bold">{formatCurrency(viewingInvoice.amount)}</span>
              </div>
              
              {viewingInvoice.notes && (
                <div>
                  <p className="text-sm text-slate-500">Notes</p>
                  <p className="text-sm">{viewingInvoice.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Invoice Preview Dialog */}
      <Dialog open={sendPreviewOpen} onOpenChange={setSendPreviewOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              Send Invoice
            </DialogTitle>
          </DialogHeader>
          {sendingInvoice && (
            <div className="space-y-6 mt-4">
              {/* Invoice Preview */}
              <div className="border rounded-lg p-6 bg-gradient-to-br from-slate-50 to-white">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900">INVOICE</h3>
                    <p className="text-slate-500 mt-1">#{sendingInvoice.invoice_number || sendingInvoice.invoice_id?.slice(-8).toUpperCase()}</p>
                  </div>
                  <Badge className="bg-amber-100 text-amber-700 text-sm">
                    {sendingInvoice.status?.toUpperCase()}
                  </Badge>
                </div>
                
                <div className="grid grid-cols-2 gap-6 mb-6">
                  <div>
                    <p className="text-sm text-slate-500">Bill To</p>
                    <p className="font-semibold">{getClientName(sendingInvoice.client_id)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-500">Amount Due</p>
                    <p className="text-3xl font-bold text-blue-600">{formatCurrency(sendingInvoice.amount)}</p>
                  </div>
                </div>
                
                {sendingInvoice.line_items && sendingInvoice.line_items.length > 0 && (
                  <div className="border-t pt-4">
                    <p className="text-sm text-slate-500 mb-2">Line Items</p>
                    <div className="space-y-2">
                      {sendingInvoice.line_items.slice(0, 3).map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="text-slate-600">{item.description?.substring(0, 40)}{item.description?.length > 40 ? '...' : ''}</span>
                          <span className="font-medium">{formatCurrency(item.amount)}</span>
                        </div>
                      ))}
                      {sendingInvoice.line_items.length > 3 && (
                        <p className="text-xs text-slate-400">+{sendingInvoice.line_items.length - 3} more items</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              <Separator />
              
              {/* Email Details */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="send_email">Send To Email *</Label>
                  <Input
                    id="send_email"
                    type="email"
                    value={sendEmailTo}
                    onChange={(e) => setSendEmailTo(e.target.value)}
                    placeholder="client@example.com"
                    data-testid="send-invoice-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="send_subject">Subject</Label>
                  <Input
                    id="send_subject"
                    value={sendSubject}
                    onChange={(e) => setSendSubject(e.target.value)}
                    data-testid="send-invoice-subject"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="send_message">Message</Label>
                  <Textarea
                    id="send_message"
                    value={sendMessage}
                    onChange={(e) => setSendMessage(e.target.value)}
                    rows={4}
                    data-testid="send-invoice-message"
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setSendPreviewOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleConfirmSend} 
                  disabled={isSending || !sendEmailTo}
                  className="gap-2"
                  data-testid="confirm-send-invoice-btn"
                >
                  {isSending ? (
                    <>
                      <span className="animate-spin">⏳</span>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Send Invoice
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Invoices;
